/* eslint-disable camelcase */
import _ from 'lodash';
import { List } from 'immutable';

import { URL } from '../helpers/CustomerEndpoint';
import config from '../config';
import { ForbiddenError, NotFoundError, BadRequestError } from '../lib/APIClient';
import APIClient from '../lib/APIClient';
import AuthenticatedAPIClient from '../lib/AuthenticatedAPIClient';
import { resolve, resolveAll } from '../helpers/LinkMapper';
import { AuthGrantTypes, Validation } from '../constants/AppConstants';

import User from '../records/User';
import UserPreferences from '../records/customer/UserPreferences';
import AuthToken from '../records/customer/AuthToken';
import PaperSeasonTicketResults from '../records/customer/PaperSeasonTicketResults';
import Address from '../records/customer/Address';
import InAppStore from "../stores/InAppStore";
import FeatureFlagStore from '../stores/FeatureFlagStore';

const CustomerService = {

    async create (registration) {
        const { fullTelephoneNumber, isOver16, preferences } = registration;
        const mutatedPrefs = Object.assign(_.omit(preferences.toJS(), ['timestamp']), { isOver16 });
        const customerData = Object.assign(_.pick(registration, [
            'title',
            'firstNames',
            'surname',
            'email',
            'password',
            'referrer'
        ]), {
            telephoneNumber: fullTelephoneNumber,
            preferences: mutatedPrefs
        });

        const response = await AuthenticatedAPIClient.post(URL.create, customerData);
        const customer = response.result;
        return resolve({ customer }).as(User);
    },

    async auth (username, password) {
        const authData = {
            username,
            password,
            grant_type: AuthGrantTypes.PASSWORD,
            ...(!InAppStore.isInApp() && {session_short: config.get('sessionShort')})
        };

        const response = await AuthenticatedAPIClient.post(URL.auth, authData);
        const token = resolve(response.result).as(AuthToken);
        return token;
    },

    async fetch (token, user) {
        const userId = token.getCustomerId() || (user && user.id);
        if (userId) {
            const response = await AuthenticatedAPIClient.get(URL.customer.replace('{id}', userId));
            // customer token misses some data present in auth token, overwrite it with the auth token
            const customer = Object.assign({}, response.result, {accessToken: token});
            return resolve({ customer }).as(User);
        }
        return null;
    },

    async logout (user) {
        if (user.isLoggedIn()) {
            try {
                await AuthenticatedAPIClient.delete(`${URL.auth}/${user.accessToken}`);
            } catch (error) {
                if (!(error instanceof NotFoundError)) {
                    throw error;
                }
            }
        }
    },

    async update (user, changes) {
        const fields = Object.keys(changes);
        const hasChanges = !_.isEqual(changes, _.pick(user.customer, fields));
        if (hasChanges) {
            const response = await AuthenticatedAPIClient.put(URL.customer.replace('{id}', user.id), changes);
            const customer = _.result(response, ['result']);
            if (customer) {
                return resolve({ customer }).as(User);
            }
        }

        return user;
    },

    async updatePreferences (userId, form, omitAge) {
        let data = Object.assign({}, form.get('preferences').toJS(), {
            telephoneNumber: form.get('telephoneNumber')
        });
        if (!omitAge) {
            data = Object.assign(data, {
                isOver16: form.get('isOver16')
            });
        }
        const result = await AuthenticatedAPIClient.post(URL.preferences.replace('{id}', userId), data);
        return resolve(result).as(UserPreferences);
    },

    async updatePreferencesV2 (userId, form) {
        const preferences = form.get('preferences').toJS();
        const data = {
            emailServiceUpdates: preferences.emailServiceUpdates,
            emailPromotions: preferences.emailPromotions,
            emailProducts: preferences.emailProducts,
            smsServiceUpdates: preferences.smsServiceUpdates,
            smsPromotions: preferences.smsPromotions,
            smsProducts: preferences.smsProducts,
        };
        await AuthenticatedAPIClient.post(URL.preferencesV2.replace('{id}', userId), data);
    },

    async patchPreferences (userId, form) {
        const result = await AuthenticatedAPIClient.patch(URL.preferences.replace('{id}', userId), form.toJS());
        return resolve(result).as(UserPreferences);
    },

    async fetchPreferences (userId) {
        const result = await AuthenticatedAPIClient.get(URL.preferences.replace('{id}', userId));
        if (FeatureFlagStore.get('enableCACIv2')) {
            const resultV2 = await AuthenticatedAPIClient.get(URL.preferencesV2.replace('{id}', userId));
            const preferences = Object.assign({}, result, resultV2);
            return resolve(preferences).as(UserPreferences);
        } else {
            return resolve(result).as(UserPreferences);
        }
    },

    async fetchEmailUserPreferences (userId, emailToken) {
        const result = await APIClient.get(URL.preferences.replace('{id}', userId), { emailToken });
        return resolve(result).as(UserPreferences);
    },

    async updateEmailUserPreferences (userId, emailToken, preferences) {
        const data = Object.assign({}, preferences.toJS(), { emailToken });
        await APIClient.post(URL.preferences.replace('{id}', userId), data);
        return Promise.resolve();
    },

    async refresh (user) {
        const refreshData = {
            grant_type: AuthGrantTypes.REFRESH_TOKEN,
            token: user.refreshToken,
            ...(!InAppStore.isInApp() && {session_short: config.get('sessionShort')})
        };
        const response = await AuthenticatedAPIClient.post(URL.refresh, refreshData);
        const token = resolve(response.result).as(AuthToken);
        return token;
    },

    async requestResetPassword (email, resetLinkQueryParams = null) {
        const response = await AuthenticatedAPIClient.post(URL.reset, {
            email,
            ...(resetLinkQueryParams && { resetLinkQueryParams })
        });
        return response;
    },

    async requestUnlockAccount (email) {
        const response = await AuthenticatedAPIClient.post(URL.reset, {
            email,
            isLocked: true,
        });
        return response;
    },

    async resetPassword (token, password) {
        const response = await AuthenticatedAPIClient.post(`${URL.reset}/?token=${token}`, {
            password
        });
        if (_.isObject(response)) {
            return resolve(response.result).as(AuthToken);
        }
        return null;
    },

    async unsubscribe (id, token) {
        await AuthenticatedAPIClient.post(`/customers/unsubscribe`, { id, token });
        return true;
    },

    async activateSmartcard (user) {
        const response = await AuthenticatedAPIClient.post(URL.activateSmartcard.replace('{id}', user.id));
        return response;
    },

    async findSeasonTickets (user, postCode, photocardId) {
        const url = URL.findSeasonTickets.replace('{id}', user.id);
        const response = await AuthenticatedAPIClient.post(url, { postCode, photocardId}, { validErrorStatuses: [404] });
        const result = _.get(response, 'result', {});
        return resolve(result).as(PaperSeasonTicketResults);
    },

    async transferSeasonTicket (user, transferForm, params) {
        const { ticket, collectionStation } = transferForm;
        const { postcode, photocardNumber } = params;

        const data = {
            origin: ticket.origin.nlc,
            destination: ticket.destination.nlc,
            route: ticket.route.code,
            ticketType: ticket.ticketType.code,
            saleTicketNumber: ticket.saleTicketNumber,
            price: ticket.price,
            validFrom: ticket.validFrom.format('YYYY-MM-DDTHH:mm:ss'),
            validTo: ticket.validTo.format('YYYY-MM-DDTHH:mm:ss'),
            validityPeriod: ticket.validityPeriod.toString(),
            ticketClass: ticket.ticketClass,
            methodOfPayment: ticket.methodOfPayment,
            fulfilmentLocation: collectionStation,
            replacement: false,
            photocardId: photocardNumber,
            postcode: postcode,
            ticketId: ticket.id
        };
        const url = URL.transferSeasonTicket.replace('{id}', user.id);
        const response = await AuthenticatedAPIClient.post(url, data);
        return response;
    },

    async lookUpAddress (postcode) {
        try {
            const response = await APIClient.get(URL.postCodeLookUp, {
                postcode: postcode.replace(/\s/g, '')
            });
            const [matches, outcode, incode] = (
                postcode.match(Validation.postcode) || []
            );
            const formattedPostcode = matches ? `${outcode} ${incode}`.toUpperCase() : postcode;
            return resolveAll(response).as(Address)
                .map(a => a.set('postCode', formattedPostcode));
        } catch (error) {
            if ((error instanceof ForbiddenError)
                || (error instanceof NotFoundError)) {
                return new List();
            }
            throw error;
        }
    },

    async validateBacs ({ sortCode, accountNumber }) {
        try {
            const data = { sortCode, accountNumber };
            await APIClient.get(URL.validateBacs, data);
            return true;
        } catch (e) {
            if (e instanceof BadRequestError) {
                return false;
            }
            throw e;
        }
    },

    async checkEmailAddress (email) {
        const response = await AuthenticatedAPIClient.get(URL.checkEmail, { email });
        return JSON.parse(response);
    },

    async deleteCustomer (userId) {
        const url = URL.deleteCustomer.replace('{id}', userId);
        await AuthenticatedAPIClient.post(url);
        return true;
    },

    async cancelDeletionRequest (userId, requestId) {
        const url = URL.cancelDeletionRequest.replace('{id}', userId).replace('{requestId}', requestId);
        await AuthenticatedAPIClient.post(url);
        return true;
    },

};

export default CustomerService;
